import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Sparkles } from 'lucide-react';
import RichTextEditor from '../RichTextEditor';
import { useNotifications } from '../../hooks/useNotifications';
import type { EquipeI18nBlock, EquipeLocaleFields } from '../../i18n/localizeContent';
import type { LocaleCode } from './I18nTripleFields';

interface I18nEquipeFieldsProps {
  value: EquipeI18nBlock;
  onChange: (next: EquipeI18nBlock) => void;
}

const LOCALES: { code: LocaleCode; labelKey: string }[] = [
  { code: 'pt', labelKey: 'admin.langPt' },
  { code: 'en', labelKey: 'admin.langEn' },
  { code: 'es', labelKey: 'admin.langEs' }
];

const I18nEquipeFields: React.FC<I18nEquipeFieldsProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const [active, setActive] = React.useState<LocaleCode>('pt');
  const [translating, setTranslating] = useState(false);
  const block = value[active] || {};

  const patch = (field: keyof EquipeLocaleFields, fieldValue: string) => {
    onChange({
      ...value,
      [active]: { ...value[active], [field]: fieldValue }
    });
  };

  const canTranslate = active !== 'pt';

  const handleTranslate = async () => {
    try {
      setTranslating(true);
      const res = await axios.post('/equipe/translate', {
        nome: value.pt?.nome || '',
        cargo: value.pt?.cargo || '',
        descricao: value.pt?.descricao || ''
      });
      const { en, es } = res.data;
      onChange({
        ...value,
        en: { ...value.en, ...en },
        es: { ...value.es, ...es }
      });
      notifications.showSuccess('Tradução gerada!', 'Revise o conteúdo em inglês e espanhol antes de salvar.');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      notifications.showError('Erro ao traduzir', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 pb-2">
        <div className="flex flex-wrap gap-2">
          {LOCALES.map((loc) => (
            <button
              key={loc.code}
              type="button"
              onClick={() => setActive(loc.code)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                active === loc.code ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t(loc.labelKey)}
            </button>
          ))}
        </div>
        {canTranslate && (
          <button
            type="button"
            onClick={handleTranslate}
            disabled={translating}
            className="flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-800 disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" />
            {translating ? 'Traduzindo...' : 'Traduzir com IA'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('admin.teamName')} {active === 'pt' && '*'}
          </label>
          <input
            type="text"
            value={block.nome || ''}
            onChange={(e) => patch('nome', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
            required={active === 'pt'}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('admin.teamRole')} {active === 'pt' && '*'}
          </label>
          <input
            type="text"
            value={block.cargo || ''}
            onChange={(e) => patch('cargo', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
            required={active === 'pt'}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.teamBio')}</label>
        <p className="text-xs text-gray-500 mb-2">{t('admin.teamBioHint')}</p>
        <RichTextEditor
          key={active}
          value={block.descricao || ''}
          onChange={(html) => patch('descricao', html)}
          placeholder={t('admin.teamBioPlaceholder')}
          minHeight="160px"
        />
      </div>
    </div>
  );
};

export default I18nEquipeFields;
